import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  FiGrid, FiList, FiBox, FiShoppingCart, FiUsers,
  FiSettings, FiLogOut, FiMenu, FiBell, FiGlobe,
  FiArchive, FiImage, FiZap, FiSearch, FiInfo, FiChevronDown,
  FiFileText, FiPackage, FiLayers, FiAlertCircle
} from 'react-icons/fi';
import brandLogo from '../../../assets/image.png';
import { useAuth } from '../../../context/AuthContext';
import { productService } from '../../../services/api';
import { categoryService } from '../../../services/api';
import { orderService } from '../../../services/api';
import './AdminLayout.css';

const AdminLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState({ products: [], categories: [], orders: [] });
  const [showResults, setShowResults] = useState(false);
  const [allDataPool, setAllDataPool] = useState({ products: [], categories: [], orders: [] });
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);

  const searchRef = useRef(null);
  const searchInputRef = useRef(null);

  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Load searchable data pool
  useEffect(() => {
    const fetchSearchData = async () => {
      try {
        const [products, categories, ordersRaw] = await Promise.all([
          productService.getAllProducts(),
          categoryService.getAllCategories(),
          orderService.getAllOrders()
        ]);

        // Normalize Orders (Backend often returns { data: [] } or { orders: [] })
        let orders = [];
        if (Array.isArray(ordersRaw)) {
          orders = ordersRaw;
        } else if (ordersRaw && Array.isArray(ordersRaw.data)) {
          orders = ordersRaw.data;
        } else if (ordersRaw && Array.isArray(ordersRaw.orders)) {
          orders = ordersRaw.orders;
        }

        setAllDataPool({
          products: products || [],
          categories: categories || [],
          orders: orders
        });
        setIsDataLoaded(true);
      } catch (err) {
        console.error("Search data fetch failed:", err);
      }
    };
    fetchSearchData();
  }, []);

  // Keyboard shortcut listener (Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setShowResults(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Click outside listener
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const performSearch = useCallback((term) => {
    if (!term.trim() || !isDataLoaded) {
      setSearchResults({ products: [], categories: [], orders: [] });
      setShowResults(false);
      return;
    }

    const t = term.toLowerCase();
    const filteredProducts = allDataPool.products.filter(p =>
      p.title?.toLowerCase().includes(t) || p._id?.toLowerCase().includes(t)
    ).slice(0, 5);

    const filteredCategories = allDataPool.categories.filter(c =>
      c.name?.toLowerCase().includes(t) || c._id?.toLowerCase().includes(t)
    ).slice(0, 3);

    const filteredOrders = allDataPool.orders.filter(o =>
      o._id?.toLowerCase().includes(t) || o.customerName?.toLowerCase().includes(t)
    ).slice(0, 3);

    setSearchResults({
      products: filteredProducts,
      categories: filteredCategories,
      orders: filteredOrders
    });
    setShowResults(true);
  }, [allDataPool, isDataLoaded]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      performSearch(searchTerm);
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm, performSearch]);

  const handleResultClick = (path) => {
    navigate(path);
    setShowResults(false);
    setSearchTerm('');
    closeSidebarOnMobile();
  };

  const handleLogout = () => {
    logout();
    closeSidebarOnMobile();
    navigate('/');
  };

  const closeSidebarOnMobile = () => {
    if (window.innerWidth <= 767) {
      setIsSidebarOpen(false);
    }
  };

  // Ensure sidebar starts closed on mobile
  useEffect(() => {
    if (window.innerWidth <= 767) {
      setIsSidebarOpen(false);
    }
  }, []);

  const navGroups = [
    {
      group: 'Main Hub',
      links: [
        { name: 'Dashboard', path: '/admin/dashboard', icon: <FiGrid /> },
        { name: 'View Store', path: `https://getitmuno.com?v=${Date.now()}`, icon: <FiGlobe />, isExternal: true },
      ]
    },
    {
      group: 'Catalog',
      links: [
        { name: 'Categories', path: '/admin/categories', icon: <FiList /> },
        { name: 'Products', path: '/admin/products', icon: <FiBox /> },
        { name: 'Sliders', path: '/admin/sliders', icon: <FiImage /> },
      ]
    },
    {
      group: 'Marketing',
      links: [
        { name: 'Manage Sales', path: '/admin/sales', icon: <FiZap /> },
      ]
    },
    {
      group: 'Operations',
      links: [
        { name: 'Orders', path: '/admin/orders', icon: <FiShoppingCart /> },
        { name: 'Inventory', path: '/admin/inventory', icon: <FiArchive /> },
        { name: 'Customers', path: '/admin/customers', icon: <FiUsers /> },
      ]
    },
    {
      group: 'System',
      links: [
        { name: 'Settings', path: '/admin/settings', icon: <FiSettings /> },
      ]
    }
  ];

  return (
    <div className={`admin-layout ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
      {/* Elite Navy Sidebar */}
      <aside className={`admin-sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
        <div className="admin-brand">
          <img src={brandLogo} alt="MUNO" className="brand-image-logo" />
        </div>

        <nav className="admin-nav">
          {navGroups.map((group, gIdx) => (
            <div key={gIdx} className="nav-group">
              <div className="nav-group-label">
                <span>{group.group}</span>
              </div>
              {group.links.map((link, idx) => (
                link.isExternal ? (
                  <a
                    key={idx}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="admin-nav-link"
                    onClick={closeSidebarOnMobile}
                  >
                    <div className="nav-icon-wrapper">
                      <div className="nav-icon">{link.icon}</div>
                    </div>
                    <span className="nav-text">{link.name}</span>
                  </a>
                ) : (
                  <NavLink
                    key={idx}
                    to={link.path}
                    onClick={closeSidebarOnMobile}
                    className={({ isActive }) => `admin-nav-link ${isActive ? 'active' : ''}`}
                  >
                    <div className="nav-icon-wrapper">
                      <div className="nav-icon">{link.icon}</div>
                    </div>
                    <span className="nav-text">{link.name}</span>
                  </NavLink>
                )
              ))}
            </div>
          ))}
        </nav>

        <div className="admin-sidebar-footer">
          <button className="admin-logout-btn" onClick={handleLogout}>
            <div className="logout-icon-wrapper">
              <FiLogOut />
            </div>
            <span className="logout-text">Logout Account</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="admin-main">
        {/* Elite Glass Header */}
        <header className="admin-header-simple">
          <div className="hs-left">
            <button
              className="hs-menu-btn"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            >
              <FiMenu />
            </button>
            <h1 className="hs-title">CMS MUNO</h1>
          </div>

          <div className="hs-center" ref={searchRef}>
            <div className={`hs-search-bar ${showResults ? 'active' : ''}`}>
              <FiSearch className="hs-search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search global products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => searchTerm && setShowResults(true)}
              />
              <div className="hs-search-kbd">⌘K</div>

              {/* Functional Search Results */}
              {showResults && (
                <div className="hs-search-results animate-in-up">
                  {searchResults.products.length === 0 && searchResults.categories.length === 0 && searchResults.orders.length === 0 ? (
                    <div className="search-empty">
                      <FiAlertCircle />
                      <span>No matches found for "{searchTerm}"</span>
                    </div>
                  ) : (
                    <>
                      {searchResults.products.length > 0 && (
                        <div className="search-section">
                          <label><FiPackage /> Products</label>
                          {searchResults.products.map(p => (
                            <div key={p._id} className="search-item" onClick={() => handleResultClick('/admin/products')}>
                              <div className="item-img"><img src={p.images?.[0]} alt="" /></div>
                              <div className="item-info">
                                <span className="item-name">{p.title}</span>
                                <span className="item-meta">PKR {p.variants?.[0]?.finalPrice || p.variants?.[0]?.price} • {p.categoryId?.name}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {searchResults.categories.length > 0 && (
                        <div className="search-section">
                          <label><FiLayers /> Categories</label>
                          {searchResults.categories.map(c => (
                            <div key={c._id} className="search-item" onClick={() => handleResultClick('/admin/categories')}>
                              <div className="item-icon-box"><FiLayers /></div>
                              <div className="item-info">
                                <span className="item-name">{c.name}</span>
                                <span className="item-meta">Category Management</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {searchResults.orders.length > 0 && (
                        <div className="search-section">
                          <label><FiFileText /> Orders</label>
                          {searchResults.orders.map(o => (
                            <div key={o._id} className="search-item" onClick={() => handleResultClick('/admin/orders')}>
                              <div className="item-icon-box"><FiShoppingCart /></div>
                              <div className="item-info">
                                <span className="item-name">Order #{o._id?.slice(-6).toUpperCase()}</span>
                                <span className="item-meta">{o.customerName || 'Customer'} • {o.status}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="hs-right">
            <div className="hs-header-utilities">
              <a
                href={`https://getitmuno.com?v=${Date.now()}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hs-util-btn hs-store-util"
                title="View Store"
              >
                <FiGlobe />
              </a>
              <button className="hs-util-btn hs-bell-util" onClick={() => setShowComingSoon(true)}>
                <FiBell /><div className="util-badge"></div>
              </button>
              <button className="hs-util-btn hs-info-util" onClick={() => setShowComingSoon(true)}>
                <FiInfo />
              </button>
            </div>
            <div className="hs-profile-professional">
              <div className="hs-profile-info">
                <span className="hs-profile-name">{user?.fullName || 'Tayyab Raza'}</span>
                <span className="hs-profile-role">Executive Administrator</span>
              </div>
              <div className="hs-profile-avatar">
                <img src={`https://ui-avatars.com/api/?name=${user?.fullName || 'TR'}&background=0f172a&color=fff&bold=true`} alt="avatar" />
                <div className="hs-status-dot"></div>
              </div>
            </div>
          </div>
        </header>

        {/* Dynamic Outlet for nested routes */}
        <div className="admin-content-wrapper">
          <Outlet />
        </div>
      </main>

      {/* Premium Coming Soon Popup */}
      {showComingSoon && (
        <div className="cs-popup-overlay" onClick={() => setShowComingSoon(false)}>
          <div className="cs-popup-card" onClick={e => e.stopPropagation()}>
            <div className="cs-icon-shell">
              <FiZap className="cs-zap" />
            </div>
            <div className="cs-badge">Admin feature</div>
            <h2>Coming Soon</h2>
            <p>Our developers are hard at work building this module. It will be available in the next elite update!</p>
            <button className="cs-close-btn" onClick={() => setShowComingSoon(false)}>
              Got it!
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLayout;

